import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth, ROLES } from '../contexts/AuthContext';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { 
  Building2, 
  ChevronDown, 
  Home, 
  Users,
  Crown,
  Loader2,
  AlertCircle,
  UserPlus,
  Briefcase
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function WorkspaceSwitcher({ compact = false }) {
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const { user, authToken } = useAuth();
  
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [slotsUsed, setSlotsUsed] = useState(0);
  const [slotsMax, setSlotsMax] = useState(2);
  const [plan, setPlan] = useState('INDEPENDENT');
  const [associateCount, setAssociateCount] = useState(0);
  const [maxAssociates, setMaxAssociates] = useState(0);
  
  // Only show for consultants
  const isConsultant = user?.role === ROLES.CONSULTANT;
  const isConsultantAssociate = user?.role === ROLES.CONSULTANT_ASSOCIATE;
  const showSwitcher = isConsultant || isConsultantAssociate;
  
  // Can add associates only if CONSULTANT (lead) and AGENCY or ENTERPRISE plan
  const canAddAssociates = isConsultant && (plan === 'AGENCY' || plan === 'ENTERPRISE');
  
  useEffect(() => {
    if (!showSwitcher || !authToken) {
      setLoading(false);
      return;
    }
    
    const fetchWorkspaces = async () => {
      try {
        const response = await fetch(`${API_URL}/api/clients/workspaces`, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          setWorkspaces(data.workspaces || []);
          setSlotsUsed(data.client_slots_used || 0);
          setSlotsMax(data.client_slots_max || 2);
          setPlan(data.consultant_plan || 'INDEPENDENT');
          setAssociateCount(data.associate_count || 0);
          setMaxAssociates(data.max_associates || 0);
        } else {
          // If endpoint fails, just show home workspace
          setWorkspaces([{
            id: user.organizationId,
            name: user.organizationName || 'My Practice',
            slug: workspaceId || 'workspace',
            type: 'HOME',
            is_current: true
          }]);
        }
      } catch (err) {
        console.error('Failed to fetch workspaces:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchWorkspaces();
  }, [authToken, showSwitcher, user, workspaceId]);
  
  if (!showSwitcher) {
    return null;
  }
  
  // Find current workspace
  const currentWorkspace = workspaces.find(w => w.slug === workspaceId) || workspaces[0];
  
  const handleWorkspaceSwitch = (workspace) => {
    if (workspace.slug === workspaceId) return;
    
    // Navigate to the new workspace dashboard
    navigate(`/${workspace.slug}/dashboard`);
  };

  const handleQuickAddAssociate = () => {
    // Navigate to settings page with team management tab
    navigate(`/${workspaceId}/settings?tab=team`);
    toast.info('Add associates from Team Management settings');
  };
  
  const getWorkspaceIcon = (type) => {
    switch (type) {
      case 'HOME':
        return <Home className="w-4 h-4 text-purple-600" />;
      case 'CLIENT':
        return <Building2 className="w-4 h-4 text-teal-600" />;
      default:
        return <Users className="w-4 h-4 text-slate-400" />;
    }
  };
  
  const getPlanBadge = () => {
    switch (plan) {
      case 'ENTERPRISE':
        return <Badge className="bg-amber-100 text-amber-700 text-xs"><Crown className="w-3 h-3 mr-1" />Enterprise</Badge>;
      case 'AGENCY':
        return <Badge className="bg-purple-100 text-purple-700 text-xs"><Users className="w-3 h-3 mr-1" />Agency</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 text-xs">Independent</Badge>;
    }
  };

  // Separate HOME and CLIENT workspaces for better organization
  const homeWorkspaces = workspaces.filter(w => w.type === 'HOME');
  const clientWorkspaces = workspaces.filter(w => w.type === 'CLIENT');
  
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {compact ? (
          <button
            type="button"
            className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-slate-400 hover:text-white hover:bg-teal-800 transition-colors"
            title={loading ? 'Loading workspaces…' : currentWorkspace?.name || 'Select workspace'}
            aria-label="Switch workspace"
            data-testid="workspace-switcher-trigger"
          >
            {getWorkspaceIcon(currentWorkspace?.type)}
          </button>
        ) : (
          <button
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-slate-700/50 transition-colors text-left"
            data-testid="workspace-switcher-trigger"
          >
            <div className="flex items-center gap-2 min-w-0">
              {getWorkspaceIcon(currentWorkspace?.type)}
              <div className="flex-1 min-w-0">
                <span className="truncate text-sm font-medium text-white block">
                  {loading ? 'Loading...' : currentWorkspace?.name || 'Select Workspace'}
                </span>
                <span className="text-[10px] text-slate-400">
                  {currentWorkspace?.type === 'HOME' ? 'Your Consultancy' : 'Client Workspace'}
                </span>
              </div>
            </div>
            <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
          </button>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span>Workspaces</span>
          {getPlanBadge()}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        
        {loading && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
          </div>
        )}
        
        {error && (
          <div className="flex items-center gap-2 px-2 py-3 text-sm text-red-600">
            <AlertCircle className="w-4 h-4" />
            Failed to load workspaces
          </div>
        )}
        
        {!loading && !error && (
          <>
            {/* HOME Workspaces Section */}
            {homeWorkspaces.length > 0 && (
              <>
                <div className="px-2 py-1.5">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <Briefcase className="w-3 h-3" />
                    Your Consultancy
                  </p>
                </div>
                {homeWorkspaces.map((workspace) => (
                  <DropdownMenuItem
                    key={workspace.id}
                    onClick={() => handleWorkspaceSwitch(workspace)}
                    className={`cursor-pointer ${workspace.slug === workspaceId ? 'bg-purple-50 border-l-2 border-l-purple-500' : ''}`}
                    data-testid={`workspace-option-${workspace.slug}`}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center flex-shrink-0">
                        <Home className="w-4 h-4 text-purple-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate text-slate-900">{workspace.name}</div>
                        <div className="text-xs text-purple-600 font-medium">Home Practice</div>
                      </div>
                      {workspace.slug === workspaceId && (
                        <Badge className="bg-purple-100 text-purple-700 text-[10px]">Active</Badge>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </>
            )}
            
            {/* CLIENT Workspaces Section */}
            {clientWorkspaces.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <div className="px-2 py-1.5">
                  <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                    <Building2 className="w-3 h-3" />
                    Client Workspaces ({clientWorkspaces.length})
                  </p>
                </div>
                {clientWorkspaces.map((workspace) => (
                  <DropdownMenuItem
                    key={workspace.id}
                    onClick={() => handleWorkspaceSwitch(workspace)}
                    className={`cursor-pointer ${workspace.slug === workspaceId ? 'bg-teal-50 border-l-2 border-l-teal-500' : ''}`}
                    data-testid={`workspace-option-${workspace.slug}`}
                  >
                    <div className="flex items-center gap-3 w-full">
                      <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center flex-shrink-0">
                        <Building2 className="w-4 h-4 text-teal-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate text-slate-900">{workspace.name}</div>
                        <div className="text-xs text-teal-600 font-medium">Client</div>
                      </div>
                      {workspace.slug === workspaceId && (
                        <Badge className="bg-teal-100 text-teal-700 text-[10px]">Active</Badge>
                      )}
                    </div>
                  </DropdownMenuItem>
                ))}
              </>
            )}
            
            {clientWorkspaces.length === 0 && (
              <div className="px-2 py-4 text-sm text-slate-500 text-center">
                No linked clients yet
              </div>
            )}
          </>
        )}
        
        <DropdownMenuSeparator />
        
        {/* Quick Add Associate Button - Only for Lead Consultants with AGENCY or ENTERPRISE plan */}
        {canAddAssociates && (
          <>
            <div className="px-2 py-2">
              <button
                onClick={handleQuickAddAssociate}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                data-testid="quick-add-associate-btn"
              >
                <UserPlus className="w-4 h-4" />
                <span>Quick Add Associate</span>
              </button>
              <div className="flex items-center justify-between mt-2 text-xs text-slate-500 px-1">
                <span>Team Members</span>
                <span className="font-medium text-slate-700">
                  {associateCount}/{maxAssociates === -1 ? '∞' : maxAssociates}
                </span>
              </div>
            </div>
            <DropdownMenuSeparator />
          </>
        )}
        
        {/* Slot Usage */}
        <div className="px-2 py-2">
          <div className="text-xs text-slate-500 mb-1">Client Slots</div>
          <div className="flex items-center gap-2">
            <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
              <div 
                className={`h-full ${slotsUsed >= slotsMax && slotsMax !== -1 ? 'bg-red-500' : 'bg-teal-500'}`}
                style={{ width: `${slotsMax === -1 ? 30 : Math.min(100, (slotsUsed / slotsMax) * 100)}%` }}
              />
            </div>
            <span className="text-xs font-medium text-slate-600">
              {slotsUsed}/{slotsMax === -1 ? '∞' : slotsMax}
            </span>
          </div>
          
          {slotsUsed >= slotsMax && slotsMax !== -1 && plan !== 'ENTERPRISE' && (
            <button
              onClick={() => navigate('/upgrade')}
              className="mt-2 w-full text-xs text-center py-1.5 bg-purple-100 text-purple-700 rounded hover:bg-purple-200 transition-colors"
            >
              Upgrade to add more clients
            </button>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
